import { useState } from 'react';
import { api } from './api';
import type { TemporaryPassword } from './api';
import { Button, ConfirmButton, Modal, ModalActions, ShowOnce, formatDate } from './ui';

/**
 * Resets another login's forgotten password. The server makes the temporary password and
 * shows it here once; there is no email to send it by, so whoever reset it passes it on.
 * Asks first, because it signs that person out wherever they are.
 */
export function ResetPasswordButton({
  userId,
  email,
  busy,
  run,
}: {
  userId: string;
  email: string;
  busy: boolean;
  /** The page's shared action runner, so its error banner and busy state cover this too. */
  run: (work: () => Promise<unknown>) => Promise<boolean>;
}) {
  const [issued, setIssued] = useState<TemporaryPassword | null>(null);

  return (
    <>
      <ConfirmButton
        label="Reset password"
        question={`Reset the password for ${email}?`}
        detail="They are signed out now, and sign in next with a temporary password you pass on."
        confirmLabel="Yes, reset"
        variant="ghost"
        busy={busy}
        onConfirm={() => void run(async () => setIssued(await api.users.resetPassword(userId)))}
      />
      <Modal open={issued !== null} title={`Temporary password for ${email}`} onClose={() => setIssued(null)}>
        {issued && (
          <>
            <ShowOnce
              label="Temporary password"
              value={issued.temporaryPassword}
              note={`Shown once - copy it now and pass it on by phone or message. It works until ${formatDate(
                issued.expiresAt,
              )}, and signing in with it asks them to choose their own password.`}
            />
            <ModalActions>
              <Button onClick={() => setIssued(null)}>Done</Button>
            </ModalActions>
          </>
        )}
      </Modal>
    </>
  );
}
